import { type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import cx from 'clsx';
import { useIntl } from 'react-intl';
import { CloseIcon } from '#assets/icons/close-icon';
import { Button } from '#components/button/button';
import styles from './top-banner.module.css';
import IconCrossRoundedFilledSmall from '@repo/monochrome-icons/icon-cross-rounded-filled-small';
import IconInfoRoundedFilled from '@repo/monochrome-icons/icon-info-rounded-filled';
import IconWarningRoundedFilledSmall from '@repo/monochrome-icons/icon-warning-rounded-filled-small';

export type TopBannerType = 'info' | 'warning' | 'error';

interface TopBannerBaseProps {
  type?: TopBannerType;
  children?: ReactNode;
  autoFocus?: boolean;
  className?: string;
  message?: string;
}

interface DismissableTopBannerProps extends TopBannerBaseProps {
  dismissable?: true;
  onClose?: () => void;
}

interface NonDismissableTopBannerProps extends TopBannerBaseProps {
  dismissable: false;
  onClose?: never;
}

export function TopBanner({
  type = 'info',
  onClose,
  children,
  autoFocus = true,
  className,
  message,
  dismissable = true,
  ...props
}: DismissableTopBannerProps | NonDismissableTopBannerProps): ReactElement {
  const { formatMessage } = useIntl();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && bannerRef.current) {
      bannerRef.current.focus();
    }
  }, [autoFocus]);

  const iconMap: Record<string, ReactNode> = {
    info: <IconInfoRoundedFilled />,
    warning: <IconWarningRoundedFilledSmall />,
    error: <IconCrossRoundedFilledSmall />,
  };

  const icon = iconMap[type];

  const isString = typeof message === 'string';

  function handleClose(): void {
    onClose?.();
  }

  return (
    <div
      className={cx(styles.topbar, styles[type], className)}
      data-test-topbanner
      data-test-topbanner-type={type}
      ref={bannerRef}
      {...props}
    >
      <div className={styles.wrapper}>
        <span className={styles.icon} data-test-topbanner-icon={type}>
          {icon}
        </span>
        {isString ? (
          <p
            className={cx(styles.message, 'body-standard')}
            dangerouslySetInnerHTML={{ __html: String(message) }}
            data-test-topbanner-message
            id="top-banner-message"
          />
        ) : (
          <p
            className={cx(styles.message, 'body-standard')}
            data-test-topbanner-message
            id="top-banner-message"
          >
            {message}
          </p>
        )}
        {children}
      </div>
      {dismissable ? (
        <Button
          aria-label={formatMessage({ id: 'a11y-buttons-close-aria-label' })}
          className={styles.close}
          data-test-topbanner-close-button
          iconOnly
          onPress={handleClose}
          type="button"
          size="small"
          variant="tertiary"
        >
          <CloseIcon aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
